// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Label } from '../ui/label';
import { EducBox, ChartBox, ResultCard, InputRow, EDUC, baseChartOpts, barLabelPlugin, fmt } from './shared';

const TFRCalc = () => {
  const [stipendio, setStipendio] = useState('30000');
  const [anniMaturati, setAnniMaturati] = useState('5');
  const [anniPensione, setAnniPensione] = useState('30');
  const [rendimento, setRendimento] = useState('3.5');
  const [aliquotaIRPEF, setAliquotaIRPEF] = useState('33');

  const S = parseFloat(stipendio) || 0;
  const AM = parseInt(anniMaturati) || 0;
  const AP = parseInt(anniPensione) || 0;
  const R = parseFloat(rendimento) / 100;

  const tfrOggi = (S / 13.5) * AM;
  // Deployment 14 — P4: tasso rivalutazione 2026 = 1,5% fisso + 75% inflazione FOI 2025 (≈ 1%) → ~2,25%
  const TFR_RIVALUTAZIONE_2026 = 0.0225;
  const tfrAzienda = tfrOggi * Math.pow(1 + TFR_RIVALUTAZIONE_2026, AP);
  const tfrFondo = tfrOggi * Math.pow(1 + R, AP);
  const vantaggio = tfrFondo - tfrAzienda;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.tfr} />
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Stipendio lordo annuo (€)" value={stipendio} onChange={setStipendio} prefix="€" />
        <InputRow label="Anni di servizio maturati" value={anniMaturati} onChange={setAnniMaturati} suffix="anni" tooltip="Anni di lavoro con questo o precedenti datori di lavoro" />
        <InputRow label="Anni al pensionamento" value={anniPensione} onChange={setAnniPensione} suffix="anni" />
        <InputRow label="Rendimento fondo pensione (%)" value={rendimento} onChange={setRendimento} suffix="%" tooltip="Media storica fondi pensione di categoria italiani: 3-4%/anno" />
        <div>
          <Label className="text-[#888780] text-sm mb-1">Aliquota IRPEF personale</Label>
          <div className="flex gap-2 mt-1">
            {['23', '33', '43'].map(a => (
              <button key={a} onClick={() => setAliquotaIRPEF(a)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${aliquotaIRPEF === a ? 'bg-[#3D7A52] text-white' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`} data-testid={`tfr-irpef-${a}`}>{a}%</button>
            ))}
          </div>
        </div>
        <div className="p-3 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
          <p className="text-xs text-[#888780]">Rivalutazione TFR in azienda</p>
          <p className="text-sm font-semibold text-[#C9A84C]">1.5% fisso/anno</p>
          <p className="text-xs text-[#888780] mt-1">Tasso fisso di legge: 1.5% + 75% inflazione ISTAT annuale</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <ResultCard label="TFR maturato ad oggi" value={`€${fmt(tfrOggi)}`} />
        <ResultCard label="TFR in azienda alla pensione" value={`€${fmt(tfrAzienda)}`} />
        <ResultCard label="TFR in fondo pensione" value={`€${fmt(tfrFondo)}`} green />
        <ResultCard
          label="Vantaggio del fondo pensione"
          value={vantaggio >= 0 ? `+€${fmt(vantaggio)}` : `-€${fmt(Math.abs(vantaggio))}`}
          gold={vantaggio >= 0}
          sub={vantaggio >= 0 ? `Il fondo pensione rende €${fmt(vantaggio)} in più` : `Il TFR in azienda rende €${fmt(Math.abs(vantaggio))} in più`}
        />
      </div>
      <ChartBox title="Confronto TFR: azienda vs fondo" height={380}>
        <Bar
          data={{
            labels: ['TFR in Azienda', 'TFR in Fondo Pensione'],
            datasets: [{ data: [Math.round(tfrAzienda), Math.round(tfrFondo)], backgroundColor: ['rgba(136,135,128,0.7)', 'rgba(61,122,82,0.8)'], borderRadius: 8, borderSkipped: false }],
          }}
          options={baseChartOpts('Valore alla pensione')}
          plugins={[barLabelPlugin]}
        />
      </ChartBox>
      <div className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8 }}>
        <p className="text-sm text-[#C9A84C] font-semibold mb-1">Novità luglio 2026</p>
        <p className="text-sm text-[#888780]">Per i neoassunti nel settore privato il TFR va al fondo pensione automaticamente dopo 6 mesi se non si sceglie diversamente. Consulta il tuo fondo pensione per informazioni precise.</p>
      </div>
      <p className="text-xs text-[#888780] mt-4 p-3 bg-[#252523] rounded-xl" data-testid="tfr-disclaimer">
        Simulazione indicativa. I rendimenti dei fondi pensione variano. Consulta il tuo ufficio HR o un CAF.
      </p>
</motion.div>
  );
};

export default TFRCalc;
